import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import * as api from './agentsDashboardAPI.js';

// Async thunks

export const fetchAgentStatus = createAsyncThunk('agentsDashboard/fetchStatus', async (_, { rejectWithValue }) => {
  try {
    const res = await api.getAgentStatus();
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchAgentUsage = createAsyncThunk('agentsDashboard/fetchUsage', async ({ since, group }, { rejectWithValue }) => {
  try {
    const res = await api.getAgentUsage({ since, group });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchAgentAudit = createAsyncThunk('agentsDashboard/fetchAudit', async ({ group, limit, status, action_type } = {}, { rejectWithValue }) => {
  try {
    const res = await api.getAgentAudit({ group, limit, status, action_type });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchAgentAuditMore = createAsyncThunk('agentsDashboard/fetchAuditMore', async ({ group, limit, offset, status, action_type } = {}, { rejectWithValue }) => {
  try {
    const res = await api.getAgentAudit({ group, limit, offset, status, action_type });
    return { data: res.data, requestedLimit: limit };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchAgentTasks = createAsyncThunk('agentsDashboard/fetchTasks', async ({ group } = {}, { rejectWithValue }) => {
  try {
    const res = await api.getAgentTasks({ group });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchAgentTaskRuns = createAsyncThunk('agentsDashboard/fetchTaskRuns', async ({ task_id, limit }, { rejectWithValue }) => {
  try {
    const res = await api.getAgentTaskRuns({ task_id, limit });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createAgentTask = createAsyncThunk('agentsDashboard/createTask', async ({ group, ...data }, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.createTask({ group, ...data });
    dispatch(fetchAgentTasks());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const updateAgentTask = createAsyncThunk('agentsDashboard/updateTask', async ({ taskId, ...data }, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.updateTask(taskId, data);
    dispatch(fetchAgentTasks());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteAgentTask = createAsyncThunk('agentsDashboard/deleteTask', async ({ taskId }, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.deleteTask(taskId);
    dispatch(fetchAgentTasks());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const pauseAgentTask = createAsyncThunk('agentsDashboard/pauseTask', async (taskId, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.pauseTask(taskId);
    dispatch(fetchAgentTasks());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const resumeAgentTask = createAsyncThunk('agentsDashboard/resumeTask', async (taskId, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.resumeTask(taskId);
    dispatch(fetchAgentTasks());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const invokeAgentAction = createAsyncThunk('agentsDashboard/invokeAgent', async ({ Group, Prompt }, { rejectWithValue }) => {
  try {
    const res = await api.invokeAgent({ Group, Prompt });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// VM host resource usage (CPU/RAM/disk). Not yet supported by every VM build, so a failure
// is expected/routine — it's tracked separately from `error` so it doesn't mask other issues.
export const fetchVmStats = createAsyncThunk('agentsDashboard/fetchVmStats', async (_, { rejectWithValue }) => {
  try {
    const res = await api.getVmStats();
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// VM reachability + uptime. Like vmStats this is best-effort — a failure means "can't tell",
// which the banner shows as unknown rather than as a page-level error.
export const fetchVmHealth = createAsyncThunk('agentsDashboard/fetchVmHealth', async (_, { rejectWithValue }) => {
  try {
    const res = await api.getVmHealth();
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Team spend in USD, already converted by Core at each agent's model rate.
export const fetchCostSummary = createAsyncThunk('agentsDashboard/fetchCostSummary', async (since = 'today', { rejectWithValue }) => {
  try {
    const res = await api.getCostSummary(since);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Emergency stop takes two halves, and both live here so every "Pause all" button in the app
// does the same thing: Core's Status gates invoke and delegation, while the VM's scheduled tasks
// have no channel to that Status and must be paused separately.
async function setScheduledTasks(fromStatus, apply) {
  try {
    const res = await api.getAgentTasks({});
    const list = Array.isArray(res?.data) ? res.data : [];
    await Promise.allSettled(list.filter((t) => (t.Status || t.status) === fromStatus).map((t) => apply(t.Id || t.id)));
  } catch {
    // Core's Status already changed; scheduled runs re-sync on the next poll.
  }
}

export const pauseAllAgentsAction = createAsyncThunk('agentsDashboard/pauseAll', async (_, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.pauseAllAgents();
    await setScheduledTasks('active', api.pauseTask);
    dispatch(fetchAgentStatus());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Undo of the above. This also resumes tasks that were paused by hand before the emergency stop —
// "resume all" is deliberately all-or-nothing, matching the button label.
export const resumeAllAgentsAction = createAsyncThunk('agentsDashboard/resumeAll', async (_, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.resumeAllAgents();
    await setScheduledTasks('paused', api.resumeTask);
    dispatch(fetchAgentStatus());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const cancelAgentRun = createAsyncThunk('agentsDashboard/cancelRun', async ({ runId, group }, { rejectWithValue }) => {
  try {
    const res = await api.cancelRun(runId, group);
    return { runId, data: res.data };
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchApprovals = createAsyncThunk('agentsDashboard/fetchApprovals', async (state = 'pending', { rejectWithValue }) => {
  try {
    const res = await api.getApprovals(state);
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const decideApprovalAction = createAsyncThunk('agentsDashboard/decideApproval', async ({ id, data }, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.decideApproval(id, data);
    dispatch(fetchApprovals());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchUnreadCounts = createAsyncThunk('agentsDashboard/fetchUnreadCounts', async (_, { rejectWithValue }) => {
  try {
    const res = await api.getUnreadCounts();
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const markAgentReadAction = createAsyncThunk('agentsDashboard/markAgentRead', async (userAgentId, { dispatch, rejectWithValue }) => {
  try {
    await api.markAgentRead(userAgentId);
    dispatch(fetchUnreadCounts());
    return userAgentId;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchIssues = createAsyncThunk('agentsDashboard/fetchIssues', async ({ status, priority, agent } = {}, { rejectWithValue }) => {
  try {
    const res = await api.getIssues({ status, priority, agent });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createIssueAction = createAsyncThunk('agentsDashboard/createIssue', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.createIssue(data);
    dispatch(fetchIssues());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const resolveIssueAction = createAsyncThunk('agentsDashboard/resolveIssue', async ({ id, data }, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.resolveIssue(id, data);
    dispatch(fetchIssues());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteIssueAction = createAsyncThunk('agentsDashboard/deleteIssue', async (id, { dispatch, rejectWithValue }) => {
  try {
    await api.deleteIssue(id);
    dispatch(fetchIssues());
    return id;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchGoals = createAsyncThunk('agentsDashboard/fetchGoals', async ({ status } = {}, { rejectWithValue }) => {
  try {
    const res = await api.getGoals({ status });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createGoalAction = createAsyncThunk('agentsDashboard/createGoal', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.createGoal(data);
    dispatch(fetchGoals());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const updateGoalAction = createAsyncThunk('agentsDashboard/updateGoal', async ({ id, data }, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.updateGoal(id, data);
    dispatch(fetchGoals());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteGoalAction = createAsyncThunk('agentsDashboard/deleteGoal', async (id, { dispatch, rejectWithValue }) => {
  try {
    await api.deleteGoal(id);
    dispatch(fetchGoals());
    return id;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const fetchDocuments = createAsyncThunk('agentsDashboard/fetchDocuments', async ({ type } = {}, { rejectWithValue }) => {
  try {
    const res = await api.getDocuments({ type });
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const createDocumentAction = createAsyncThunk('agentsDashboard/createDocument', async (data, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.createDocument(data);
    dispatch(fetchDocuments());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const updateDocumentAction = createAsyncThunk('agentsDashboard/updateDocument', async ({ id, data }, { dispatch, rejectWithValue }) => {
  try {
    const res = await api.updateDocument(id, data);
    dispatch(fetchDocuments());
    return res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

export const deleteDocumentAction = createAsyncThunk('agentsDashboard/deleteDocument', async (id, { dispatch, rejectWithValue }) => {
  try {
    await api.deleteDocument(id);
    dispatch(fetchDocuments());
    return id;
  } catch (err) {
    return rejectWithValue(err?.response?.data || err.message);
  }
});

// Normalize camelCase API response to PascalCase used by components
function toPascal(obj) {
  if (Array.isArray(obj)) return obj.map(toPascal);
  if (!obj || typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const pk = k.charAt(0).toUpperCase() + k.slice(1);
    out[pk] = v;
  }
  return out;
}

// Slice

const agentsDashboardSlice = createSlice({
  name: 'agentsDashboard',
  initialState: {
    status: null,
    usage: null,
    vmStats: null,
    vmStatsSupported: null, // null = unknown yet, false = VM returned an error (feature not available)
    vmHealth: null,
    costSummary: null,
    cancellingRunIds: [],
    audit: [],
    auditHasMore: true,
    auditOffset: 0,
    auditFilter: { status: null, action_type: null },
    tasks: [],
    taskRuns: null,
    approvals: [],
    unreadCounts: [],
    issues: [],
    goals: [],
    documents: [],
    loading: {
      status: false,
      usage: false,
      vmStats: false,
      costSummary: false,
      pausingAll: false,
      audit: false,
      auditMore: false,
      tasks: false,
      taskRuns: false,
      approvals: false,
      deciding: false,
      issues: false,
      goals: false,
      documents: false,
    },
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTaskRuns: (state) => {
      state.taskRuns = null;
    },
    setAuditFilter: (state, { payload }) => {
      state.auditFilter = { ...state.auditFilter, ...payload };
      state.audit = [];
      state.auditOffset = 0;
      state.auditHasMore = true;
    },
    resetAudit: (state) => {
      state.audit = [];
      state.auditOffset = 0;
      state.auditHasMore = true;
    },
  },
  extraReducers: (builder) => {
    // Status
    builder
      .addCase(fetchAgentStatus.pending, (state) => {
        state.loading.status = true;
        state.error = null;
      })
      .addCase(fetchAgentStatus.fulfilled, (state, { payload }) => {
        state.loading.status = false;
        state.status = payload ? { ...payload, Groups: toPascal(payload.Groups || payload.groups) } : payload;
      })
      .addCase(fetchAgentStatus.rejected, (state, { payload }) => {
        state.loading.status = false;
        state.error = payload;
      });

    // Usage
    builder
      .addCase(fetchAgentUsage.pending, (state) => {
        state.loading.usage = true;
        state.error = null;
      })
      .addCase(fetchAgentUsage.fulfilled, (state, { payload }) => {
        state.loading.usage = false;
        state.usage = toPascal(payload);
      })
      .addCase(fetchAgentUsage.rejected, (state, { payload }) => {
        state.loading.usage = false;
        state.error = payload;
      });

    // VM host stats (CPU/RAM/disk) — failure just means "not available", not a global error.
    builder
      .addCase(fetchVmStats.pending, (state) => {
        state.loading.vmStats = true;
      })
      .addCase(fetchVmStats.fulfilled, (state, { payload }) => {
        state.loading.vmStats = false;
        state.vmStats = toPascal(payload);
        state.vmStatsSupported = true;
      })
      .addCase(fetchVmStats.rejected, (state) => {
        state.loading.vmStats = false;
        state.vmStats = null;
        state.vmStatsSupported = false;
      });

    // VM health — a failure means "unknown", not a page error (same treatment as vmStats).
    builder
      .addCase(fetchVmHealth.fulfilled, (state, { payload }) => {
        state.vmHealth = toPascal(payload);
      })
      .addCase(fetchVmHealth.rejected, (state) => {
        state.vmHealth = null;
      });

    // Cost summary — ByAgent is a list, so its rows need their own key conversion.
    builder
      .addCase(fetchCostSummary.pending, (state) => {
        state.loading.costSummary = true;
      })
      .addCase(fetchCostSummary.fulfilled, (state, { payload }) => {
        state.loading.costSummary = false;
        state.costSummary = payload ? { ...toPascal(payload), ByAgent: toPascal(payload.byAgent || payload.ByAgent || []) } : null;
      })
      .addCase(fetchCostSummary.rejected, (state) => {
        state.loading.costSummary = false;
        state.costSummary = null;
      });

    // Pause all / resume all
    builder
      .addCase(pauseAllAgentsAction.pending, (state) => {
        state.loading.pausingAll = true;
        state.error = null;
      })
      .addCase(pauseAllAgentsAction.fulfilled, (state) => {
        state.loading.pausingAll = false;
      })
      .addCase(pauseAllAgentsAction.rejected, (state, { payload }) => {
        state.loading.pausingAll = false;
        state.error = payload;
      })
      .addCase(resumeAllAgentsAction.pending, (state) => {
        state.loading.pausingAll = true;
        state.error = null;
      })
      .addCase(resumeAllAgentsAction.fulfilled, (state) => {
        state.loading.pausingAll = false;
      })
      .addCase(resumeAllAgentsAction.rejected, (state, { payload }) => {
        state.loading.pausingAll = false;
        state.error = payload;
      });

    // Cancel a running run — track in-flight ids so the UI can disable just that row's button.
    builder
      .addCase(cancelAgentRun.pending, (state, { meta }) => {
        state.cancellingRunIds.push(meta.arg.runId);
      })
      .addCase(cancelAgentRun.fulfilled, (state, { payload }) => {
        state.cancellingRunIds = state.cancellingRunIds.filter((id) => id !== payload.runId);
      })
      .addCase(cancelAgentRun.rejected, (state, { meta, payload }) => {
        state.cancellingRunIds = state.cancellingRunIds.filter((id) => id !== meta.arg.runId);
        state.error = payload;
      });

    // Audit (replace)
    builder
      .addCase(fetchAgentAudit.pending, (state) => {
        state.loading.audit = true;
        state.error = null;
      })
      .addCase(fetchAgentAudit.fulfilled, (state, { payload }) => {
        state.loading.audit = false;
        const entries = payload?.entries || payload;
        const normalized = toPascal(Array.isArray(entries) ? entries : []) || [];
        state.audit = normalized;
        state.auditOffset = normalized.length;
        state.auditHasMore = normalized.length >= 50;
      })
      .addCase(fetchAgentAudit.rejected, (state, { payload }) => {
        state.loading.audit = false;
        state.error = payload;
      });

    // Audit More (append)
    builder
      .addCase(fetchAgentAuditMore.pending, (state) => {
        state.loading.auditMore = true;
      })
      .addCase(fetchAgentAuditMore.fulfilled, (state, { payload }) => {
        state.loading.auditMore = false;
        const entries = payload.data?.entries || payload.data;
        const normalized = toPascal(Array.isArray(entries) ? entries : []) || [];
        // Deduplicate by Id
        const existingIds = new Set(state.audit.map((e) => e.Id));
        const newEntries = normalized.filter((e) => !existingIds.has(e.Id));
        state.audit = [...state.audit, ...newEntries];
        state.auditOffset = state.audit.length;
        state.auditHasMore = normalized.length >= (payload.requestedLimit || 50);
      })
      .addCase(fetchAgentAuditMore.rejected, (state, { payload }) => {
        state.loading.auditMore = false;
        state.error = payload;
      });

    // Tasks
    builder
      .addCase(fetchAgentTasks.pending, (state) => {
        state.loading.tasks = true;
        state.error = null;
      })
      .addCase(fetchAgentTasks.fulfilled, (state, { payload }) => {
        state.loading.tasks = false;
        // Core's proxy always wraps the collection — { tasks: [...] }, and { tasks: [] } when the VM
        // answers with a shape it does not recognise. Storing the payload itself put an object in
        // state, so the first tasks.slice in OverviewTab threw and no plan was ever read. Same
        // unwrap as audit above.
        const rows = payload?.tasks || payload;
        state.tasks = toPascal(Array.isArray(rows) ? rows : []) || [];
      })
      .addCase(fetchAgentTasks.rejected, (state, { payload }) => {
        state.loading.tasks = false;
        state.error = payload;
      });

    // Task Runs
    builder
      .addCase(fetchAgentTaskRuns.pending, (state) => {
        state.loading.taskRuns = true;
        state.error = null;
      })
      .addCase(fetchAgentTaskRuns.fulfilled, (state, { payload }) => {
        state.loading.taskRuns = false;
        state.taskRuns = toPascal(payload);
      })
      .addCase(fetchAgentTaskRuns.rejected, (state, { payload }) => {
        state.loading.taskRuns = false;
        state.error = payload;
      });

    // Approvals
    builder
      .addCase(fetchApprovals.pending, (state) => {
        state.loading.approvals = true;
        state.error = null;
      })
      .addCase(fetchApprovals.fulfilled, (state, { payload }) => {
        state.loading.approvals = false;
        state.approvals = toPascal(payload) || [];
      })
      .addCase(fetchApprovals.rejected, (state, { payload }) => {
        state.loading.approvals = false;
        state.error = payload;
      })
      .addCase(decideApprovalAction.pending, (state) => {
        state.loading.deciding = true;
      })
      .addCase(decideApprovalAction.fulfilled, (state) => {
        state.loading.deciding = false;
      })
      .addCase(decideApprovalAction.rejected, (state, { payload }) => {
        state.loading.deciding = false;
        state.error = payload;
      });

    // Unread counts (proactive-message badge)
    builder.addCase(fetchUnreadCounts.fulfilled, (state, { payload }) => {
      state.unreadCounts = toPascal(payload) || [];
    });

    // Issues
    builder
      .addCase(fetchIssues.pending, (state) => {
        state.loading.issues = true;
        state.error = null;
      })
      .addCase(fetchIssues.fulfilled, (state, { payload }) => {
        state.loading.issues = false;
        state.issues = toPascal(payload) || [];
      })
      .addCase(fetchIssues.rejected, (state, { payload }) => {
        state.loading.issues = false;
        state.error = payload;
      });

    // Goals
    builder
      .addCase(fetchGoals.pending, (state) => {
        state.loading.goals = true;
        state.error = null;
      })
      .addCase(fetchGoals.fulfilled, (state, { payload }) => {
        state.loading.goals = false;
        state.goals = toPascal(payload) || [];
      })
      .addCase(fetchGoals.rejected, (state, { payload }) => {
        state.loading.goals = false;
        state.error = payload;
      });

    // Documents
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.loading.documents = true;
        state.error = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, { payload }) => {
        state.loading.documents = false;
        state.documents = toPascal(payload) || [];
      })
      .addCase(fetchDocuments.rejected, (state, { payload }) => {
        state.loading.documents = false;
        state.error = payload;
      });
  },
});

export const { clearError, clearTaskRuns, setAuditFilter, resetAudit } = agentsDashboardSlice.actions;

// Selectors

export const selectAgentStatus = (state) => state.agentsDashboard.status;
export const selectAgentUsage = (state) => state.agentsDashboard.usage;
export const selectAgentVmStats = (state) => state.agentsDashboard.vmStats;
export const selectAgentVmStatsSupported = (state) => state.agentsDashboard.vmStatsSupported;
export const selectAgentVmHealth = (state) => state.agentsDashboard.vmHealth;
export const selectAgentCostSummary = (state) => state.agentsDashboard.costSummary;
export const selectCancellingRunIds = (state) => state.agentsDashboard.cancellingRunIds;
export const selectAgentAudit = (state) => state.agentsDashboard.audit;
export const selectAgentAuditHasMore = (state) => state.agentsDashboard.auditHasMore;
export const selectAgentAuditOffset = (state) => state.agentsDashboard.auditOffset;
export const selectAgentAuditFilter = (state) => state.agentsDashboard.auditFilter;
export const selectAgentTasks = (state) => state.agentsDashboard.tasks;
export const selectAgentTaskRuns = (state) => state.agentsDashboard.taskRuns;
export const selectAgentApprovals = (state) => state.agentsDashboard.approvals;
export const selectAgentUnreadCounts = (state) => state.agentsDashboard.unreadCounts;
export const selectAgentIssues = (state) => state.agentsDashboard.issues;
export const selectAgentGoals = (state) => state.agentsDashboard.goals;
export const selectAgentDocuments = (state) => state.agentsDashboard.documents;
export const selectAgentLoading = (state) => state.agentsDashboard.loading;
export const selectAgentError = (state) => state.agentsDashboard.error;

export default agentsDashboardSlice.reducer;
